import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CreditCard, Landmark, Copy, CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import { useLang } from "@/i18n";
import { API, GARAGE } from "@/config";
import { toast } from "sonner";

export default function Betalen() {
  const { rid } = useParams();
  const { t } = useLang();
  const [info, setInfo] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${API}/pay/${rid}`)
      .then(async (r) => {
        if (!r.ok) throw new Error();
        setInfo(await r.json());
      })
      .catch(() => setNotFound(true));
  }, [rid]);

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(""), 1500);
  };

  const payOnline = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ request_id: rid, origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "err");
      window.location.href = data.checkout_url;
    } catch {
      toast.error(t("pay.err"));
      setLoading(false);
    }
  };

  return (
    <div className="container-x py-14 max-w-xl" data-testid="betalen-page">
      <p className="overline-red">Milano Worx</p>
      <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-white">{t("pay.title")}</h1>

      {notFound && (
        <div className="mt-8 card-dark border-red-800/50 p-6 text-center" data-testid="pay-notfound">
          <AlertTriangle size={32} className="mx-auto text-red-400 mb-3" />
          <p className="text-zinc-300">404</p>
        </div>
      )}

      {!info && !notFound && (
        <div className="mt-12 flex justify-center"><Loader2 size={32} className="animate-spin text-[#E11D48]" /></div>
      )}

      {info && (
        <div className="mt-6 space-y-5">
          <p className="text-zinc-400 text-sm leading-relaxed">{t("pay.sub")}</p>

          <div className="card-dark p-5" data-testid="pay-summary">
            <div className="flex justify-between gap-4 text-sm border-b border-zinc-800 pb-3">
              <span className="text-zinc-500">{t("pay.service")}</span>
              <span className="text-zinc-200 text-right">{info.summary || info.type}</span>
            </div>
            <div className="flex justify-between gap-4 text-sm pt-3">
              <span className="text-zinc-500">{t("pay.ref")}</span>
              <span className="font-mono2 text-zinc-200" data-testid="pay-reference">{info.reference}</span>
            </div>
          </div>

          {info.deposit_paid ? (
            <div className="card-dark border-green-700/50 p-6 text-center" data-testid="pay-already-paid">
              <CheckCircle2 size={36} className="mx-auto text-[#22C55E] mb-3" />
              <p className="text-zinc-200 font-medium">{t("pay.alreadyPaid")}</p>
            </div>
          ) : !info.deposit_requested ? (
            <div className="card-dark border-[#D97706]/40 p-5 flex items-start gap-3" data-testid="pay-waiting">
              <AlertTriangle size={18} className="text-[#D97706] shrink-0 mt-0.5" />
              <p className="text-sm text-zinc-300">{t("pay.waitConfirm")}</p>
            </div>
          ) : (
            <>
              <button onClick={payOnline} disabled={loading} data-testid="pay-online-btn"
                className="btn-red w-full !py-4 !text-base disabled:opacity-50">
                {loading ? <Loader2 size={18} className="animate-spin" /> : <CreditCard size={18} />}
                {loading ? t("pay.redirecting") : t("pay.online")}
              </button>
              <p className="text-xs text-zinc-500 text-center -mt-2">{t("pay.onlineDesc")}</p>

              <div className="card-dark p-5" data-testid="pay-transfer">
                <h3 className="flex items-center gap-2 font-heading font-bold text-white">
                  <Landmark size={17} className="text-[#E11D48]" />{t("pay.transfer")}
                </h3>
                <div className="mt-4 space-y-3">
                  {[
                    [t("pay.iban"), GARAGE.iban, "iban"],
                    [t("pay.beneficiary"), GARAGE.beneficiary, "ben"],
                    [t("pay.communication"), `MW-${info.reference}`, "ref"],
                  ].map(([label, value, key]) => (
                    <div key={key} className="flex items-center justify-between gap-3 border-b border-zinc-800/60 pb-2.5 last:border-0">
                      <div>
                        <span className="label-dark !mb-0.5">{label}</span>
                        <span className="font-mono2 text-sm text-zinc-100" data-testid={`pay-${key}`}>{value}</span>
                      </div>
                      <button onClick={() => copy(value, key)} data-testid={`pay-copy-${key}`}
                        className="p-2 rounded-lg bg-white/5 text-zinc-400 hover:text-white transition-colors shrink-0">
                        {copied === key ? <span className="text-xs text-green-400 font-bold">{t("pay.copied")}</span> : <Copy size={15} />}
                      </button>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-xs text-zinc-500">{t("pay.transferNote")}</p>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
